/**
 * Native file picker that works in Chrome's installed app / PWA on macOS.
 *
 * A hidden `input[type=file]` inside the CSS-transformed task modal is ignored
 * there, or the OS dialog opens behind the app window. Installed Chrome uses
 * `showOpenFilePicker` (window-attached). Everyone else gets a body-level
 * input that is only visually hidden — never `display: none`.
 */

function isAbort(err) {
  return err?.name === "AbortError" || err?.code === 20;
}

export function isInstalledAppWindow(win = typeof window !== "undefined" ? window : null) {
  if (!win) return false;
  try {
    if (win.matchMedia?.("(display-mode: standalone)")?.matches) return true;
    if (win.matchMedia?.("(display-mode: window-controls-overlay)")?.matches) return true;
    if (win.matchMedia?.("(display-mode: minimal-ui)")?.matches) return true;
  } catch {
    /* ignore */
  }
  return win.navigator?.standalone === true;
}

function canUseShowOpenFilePicker(win = typeof window !== "undefined" ? window : null) {
  return Boolean(win && typeof win.showOpenFilePicker === "function" && isInstalledAppWindow(win));
}

async function pickWithFileSystemAccess({ multiple }) {
  const handles = await window.showOpenFilePicker({
    multiple: Boolean(multiple),
    excludeAcceptAllOption: false,
  });
  const files = [];
  for (const handle of handles || []) {
    if (handle && typeof handle.getFile === "function") {
      const file = await handle.getFile();
      if (file) files.push(file);
    }
  }
  return files;
}

function applyFallbackInputStyle(input) {
  Object.assign(input.style, {
    position: "fixed",
    left: "0px",
    top: "0px",
    width: "1px",
    height: "1px",
    opacity: "0.01",
    padding: "0px",
    margin: "0px",
    overflow: "hidden",
    border: "0",
    zIndex: "2147483647",
  });
}

let fallbackInput = null;
let settleFallback = null;

function finishFallback(files) {
  const settle = settleFallback;
  settleFallback = null;
  if (typeof settle === "function") settle(files || []);
}

function getFallbackInput() {
  if (fallbackInput && fallbackInput.isConnected) return fallbackInput;
  const input = document.createElement("input");
  input.type = "file";
  input.setAttribute("data-xpro-file-picker", "1");
  applyFallbackInputStyle(input);
  input.addEventListener("change", () => {
    finishFallback(Array.from(input.files || []));
    input.value = "";
  });
  input.addEventListener("cancel", () => finishFallback([]));
  document.body.appendChild(input);
  fallbackInput = input;
  return input;
}

function pickWithBodyInput({ multiple, accept }) {
  return new Promise((resolve) => {
    finishFallback([]);
    settleFallback = resolve;
    const input = getFallbackInput();
    input.multiple = Boolean(multiple);
    if (accept) input.setAttribute("accept", accept);
    else input.removeAttribute("accept");
    input.value = "";
    input.click();
  });
}

export async function pickLocalFiles({ multiple = false, accept } = {}) {
  if (typeof document === "undefined") return [];

  if (canUseShowOpenFilePicker()) {
    try {
      return await pickWithFileSystemAccess({ multiple });
    } catch (err) {
      if (isAbort(err)) return [];
    }
  }

  return pickWithBodyInput({ multiple, accept });
}

export const TASK_COMMENT_FILE_ACCEPT =
  "image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar";
